import { useState } from 'react';
import { Lock } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { Switch } from '@/components/ui/Switch';
import { Tooltip } from '@/components/ui/Tooltip';
import { TEAM_STRINGS } from '@/lib/constants/team-strings';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import type { DataRoomCurrentAccess } from '@/lib/types/data-room';
import type { TeamMember } from '../api/team.api';
import { useAssignSeatTwo } from '../hooks/useAssignSeatTwo';
import { useReleaseSeatTwo } from '../hooks/useReleaseSeatTwo';

const COPY = TEAM_STRINGS.DATA_ROOM_SEATS;
/** Membership and account status a seat holder must have (Card 11). */
const ACTIVE = 'active';

export interface DataRoomSeatCellProps {
  entityId: string;
  member: TeamMember;
  /** The startup's open access period (Premium's two seats, or an Issue Report's one). */
  access: DataRoomCurrentAccess;
}

/**
 * One member's cell in the Data Room Access column (FR-139):
 *
 * - Seat 1 holder: no toggle, a lock and "Locked". Seat 1 never moves from
 *   Team Access (A-115).
 * - Issue Report (one seat): everyone else reads "Not transferable", with no
 *   toggle, since the buyer's seat cannot move (FR-138).
 * - Seat 2 holder: on. The seat 1 holder can switch it off (after a confirm)
 *   to free the seat.
 * - Anyone else: off. The seat 1 holder can switch on one active member while
 *   seat 2 is empty; while it is taken the toggle is disabled and says why.
 *
 * Who may change seat 2 comes from the API (`canAssignSeatTwo`), never
 * re-derived here.
 */
export function DataRoomSeatCell({ entityId, member, access }: DataRoomSeatCellProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const assign = useAssignSeatTwo(entityId);
  const release = useReleaseSeatTwo(entityId);
  const seat = access.seats.find((s) => s.holder?.userId === member.userId);
  const seatTwoHolder = access.seats.find((s) => s.slot === 2)?.holder ?? null;
  const label = COPY.TOGGLE_LABEL.replace('{name}', member.name);

  if (seat?.locked) {
    return (
      <Tooltip content={COPY.LOCKED_HINT[access.seatLimit]}>
        <span className="text-text-secondary inline-flex items-center gap-1.5 text-xs font-medium">
          <Lock aria-hidden="true" className="size-3.5" />
          {COPY.LOCKED}
        </span>
      </Tooltip>
    );
  }

  if (access.seatLimit === 1) {
    return <span className="text-text-muted text-xs">{COPY.NOT_TRANSFERABLE}</span>;
  }

  if (seat) {
    const closeConfirm = (next: boolean) => {
      if (!next) release.reset();
      setConfirmOpen(next);
    };
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Switch
          checked
          label={label}
          disabled={!access.canAssignSeatTwo}
          onCheckedChange={() => setConfirmOpen(true)}
        />
        {seat.holder?.needsCompanyEmail && (
          <Badge variant="warning" size="sm">
            {COPY.NEEDS_COMPANY_EMAIL}
          </Badge>
        )}
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={closeConfirm}
          title={COPY.RELEASE_TITLE}
          description={COPY.RELEASE_BODY.replace('{name}', member.name)}
          confirmLabel={COPY.RELEASE_CONFIRM}
          isConfirming={release.isPending}
          onConfirm={() => release.mutate(member.userId, { onSuccess: () => setConfirmOpen(false) })}
        >
          {release.isError && (
            <p role="alert" className="text-danger text-sm">
              {getApiErrorMessage(release.error, COPY.FAILED)}
            </p>
          )}
        </ConfirmDialog>
      </div>
    );
  }

  const eligible = member.status === ACTIVE && member.accountStatus === ACTIVE;
  const seatTaken = seatTwoHolder !== null;
  const toggle = (
    <Switch
      checked={false}
      label={label}
      disabled={!access.canAssignSeatTwo || !eligible || seatTaken || assign.isPending}
      onCheckedChange={() => assign.mutate({ userId: member.userId, expectedHolderUserId: null })}
    />
  );
  // Explain the one disabled state the seat 1 holder can act on.
  return access.canAssignSeatTwo && eligible && seatTaken ? (
    <Tooltip content={COPY.FULL_HINT}>
      <span className="inline-flex">
        {toggle}
      </span>
    </Tooltip>
  ) : (
    toggle
  );
}
